import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import merge from "lodash/merge";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { deepClone } from "app/utils/objectHelpers";
import { getDsokSystemSettings, saveDsokSystemSettings } from "app/services/api/dsokSystemSettingsApi";
import {
  DEFAULT_COLORS,
  COLOR_FIELD_KEYS,
} from "app/pages/customers/dsok/smartkioskFields/colorsFieldConfig";
import { ColorsFieldsGrid } from "app/pages/customers/dsok/smartkioskFields/SmartkioskColors";

const HEX_REGEX = /^#([A-Fa-f0-9]{3}){1,2}$/;
function isValidHex(value) {
  if (value == null || String(value).trim() === "") return true;
  return HEX_REGEX.test(String(value).trim());
}

function validateColors(values, t) {
  const errors = {};
  if (!values.colors || typeof values.colors !== "object") return errors;
  const invalidKeys = COLOR_FIELD_KEYS.filter((key) => !isValidHex(values.colors[key]));
  const message = t("pages.systemSettings.colors.invalidHex", { defaultValue: "Neplatný hex formát (#RRGGBB)" });
  if (invalidKeys.length > 0) {
    errors.colors = invalidKeys.reduce((acc, key) => {
      acc[key] = message;
      return acc;
    }, {});
  }
  return errors;
}

export default function NewCustomerColorsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDsokSystemSettings()
      .then((data) => {
        if (!cancelled && data != null && typeof data === "object") {
          setOriginalSettings(deepClone(data));
        } else if (!cancelled) {
          setOriginalSettings(deepClone({ colors: {} }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          enqueueSnackbar(t("pages.systemSettings.loadError", { defaultValue: "Nepodarilo sa načítať nastavenia." }), {
            variant: "error",
          });
          setOriginalSettings(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enqueueSnackbar, t]);

  const initialValues = useMemo(() => {
    const colors = originalSettings?.colors;
    return {
      colors: merge({ ...DEFAULT_COLORS }, colors || {}),
    };
  }, [originalSettings]);

  const handleSubmit = useCallback(
    async (values) => {
      if (originalSettings == null) return;
      setSaving(true);
      try {
        const payloadToSave = deepClone(originalSettings);
        payloadToSave.colors = deepClone(values.colors ?? {});
        const response = await saveDsokSystemSettings(payloadToSave);
        const nextOriginal = response != null && typeof response === "object" ? deepClone(response) : deepClone(payloadToSave);
        setOriginalSettings(nextOriginal);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
        enqueueSnackbar(t("pages.systemSettings.colorsSaveSuccess", { defaultValue: "Farby boli uložené" }), {
          variant: "success",
        });
      } catch (err) {
        const message = err?.response?.data?.message ?? err?.message ?? t("pages.systemSettings.saveError", { defaultValue: "Nepodarilo sa uložiť nastavenia." });
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setSaving(false);
      }
    },
    [originalSettings, enqueueSnackbar, t]
  );

  const saveLabel = t("common.save", { defaultValue: "Uložiť" });
  const savedLabel = t("common.saved", { defaultValue: "Uložené." });

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => validateColors(values, t)}
      onSubmit={handleSubmit}
    >
      {({ dirty, handleSubmit: formikSubmit }) => (
        <Box sx={{ px: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 2,
            }}
          >
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{ flex: "1 1 auto", minWidth: 0 }}
            >
              <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
                {t("sidebar.menuItem.home")}
              </Link>
              <Link
                to="/dsok/system-settings"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {t("pages.systemSettings.breadcrumb", { defaultValue: "Nastavenia" })}
              </Link>
              <Link
                to="/dsok/system-settings/new-customer-settings"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {t("pages.systemSettings.newCustomerSettingsBreadcrumb", {
                  defaultValue: "Nastavenie nového zákazníka",
                })}
              </Link>
              <Typography color="text.primary">
                {t("pages.systemSettings.newCustomerSettingsTabs.colors", {
                  defaultValue: "Farby",
                })}
              </Typography>
            </Breadcrumbs>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}
            >
              <Tooltip title={saveLabel}>
                <span>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    type="submit"
                    form="new-customer-colors-form"
                    disabled={originalSettings == null || !dirty || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                    sx={{
                      ...((originalSettings == null || !dirty || saving) && {
                        opacity: 0.5,
                        cursor: "not-allowed",
                        "&:hover": { backgroundColor: "transparent" },
                      }),
                    }}
                  >
                    {saveLabel}
                  </Button>
                </span>
              </Tooltip>
              {saveSuccess && (
                <Typography component="span" color="success.main" variant="body2">
                  {savedLabel}
                </Typography>
              )}
            </Stack>
          </Box>
          <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
            {t("pages.systemSettings.newCustomerSettingsTabs.colors", {
              defaultValue: "Farby",
            })}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              bgcolor: "background.paper",
              p: 2,
              borderRadius: 1,
            }}
          >
            <Form id="new-customer-colors-form" onSubmit={formikSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ColorsFieldsGrid namePrefix="colors" />
                </Grid>
              </Grid>
            </Form>
          </Paper>
        </Box>
      )}
    </Formik>
  );
}
